import { Flex, Line } from 'components';
import { InfoTooltip } from 'components/InfoTooltip/InfoTooltip';
import { H3, P } from 'components/Typography/Typography';
import {
  Button,
  FieldLabel,
  Radio,
  Select,
  SwitchButton,
  TextField,
  UserSelect
} from 'components/_form';
import { Controller, useFormContext } from 'react-hook-form';
import { IOption } from 'components/_form/Select/Select';
import { IAuditForm, IPostAuditFormRequest } from 'types/audits';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft,
  faCopy,
  faDownload,
  faPersonCircleCheck,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { IOption as OptionForNestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import { PadlockButton } from 'components/_form/PadlockButton/PadlockButton';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  NestedSelect,
  IOption as OptionForSelect
} from 'components/_form/NestedSelect/NestedSelect';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { usePracticesQuery } from 'hooks/queries/usePracticesQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { useAccount } from 'hooks/useAccount';
import {
  makeMethodologyPracticeTree,
  parseMethodologyPracticeTreeForPinnedToSelect
} from 'utilities/practices';
import {
  parsePinnedToSelectValue,
  pinnedToSelectValue,
  PinnedToType
} from 'utilities/pinnedTo';
import {
  mapUsersToSelectOptions,
  mergeUserSelectOptions
} from 'utilities/userSelectOption';
import { RatingScaleButtons } from 'components/RatingScaleButtons/RatingScaleButtons';
import { IRatingScale } from 'types/ratingScales';
import { CopyForm } from '../CopyForm/CopyForm';
import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { NotesModal } from 'components/NotesModal/NotesModal';
import { AuditFormCodeNameField } from '../AuditFormCodeNameField/AuditFormCodeNameField';
import { AuditFormClosingModal } from '../AuditFormClosingModal/AuditFormClosingModal';
import {
  AuditFormApprovalSchemeModal,
  AuditFormApprovalSchemeValues
} from '../AuditFormApprovalSchemeModal/AuditFormApprovalSchemeModal';

interface Props {
  ratingScales: IRatingScale[];
  selectedAuditForm?: IAuditForm;
  setSelectedAuditForm?: Dispatch<SetStateAction<IAuditForm | undefined>>;
  showCopyButton?: boolean;
}

export const BasicSettings: React.FC<Props> = ({
  ratingScales,
  selectedAuditForm,
  setSelectedAuditForm,
  showCopyButton
}) => {
  const { data: usersData = [] } = useUsersQuery();
  const usersList = useMemo(
    () => mapUsersToSelectOptions(usersData),
    [usersData]
  );
  const { loggedUserData } = useAccount();
  const {
    control,
    register,
    getValues,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<IPostAuditFormRequest['audit_form']>();

  const authorId = watch('author_id');
  // Lustro / autor spoza GET /users — inaczej "Utworzył(a)" pokazuje pusty "Wybierz..."
  const usersListWithAuthor = useMemo(
    () =>
      mergeUserSelectOptions(usersList, [
        selectedAuditForm?.author,
        authorId?.toString() === loggedUserData.id.toString()
          ? {
              id: loggedUserData.id,
              first_name: loggedUserData.firstName,
              last_name: loggedUserData.lastName,
              email: loggedUserData.email
            }
          : null
      ]),
    [
      usersList,
      selectedAuditForm?.author,
      authorId,
      loggedUserData.id,
      loggedUserData.firstName,
      loggedUserData.lastName,
      loggedUserData.email
    ]
  );
  const { data: areasData = [] } = useAreasQuery();
  const areas = useMemo<OptionForSelect[]>(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );
  const [methodologyTreeForSelect, setMethodologyTreeForSelect] = useState<
    OptionForSelect[]
  >([]);
  const { data: practicesData = [] } = usePracticesQuery();
  const [showCopyForm, setShowCopyForm] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isClosingModalOpen, setIsClosingModalOpen] = useState(false);
  const [isApprovalSchemeModalOpen, setIsApprovalSchemeModalOpen] =
    useState(false);

  const requiresApproval = !!watch('requires_approval');
  const executionProgressTrackingId = watch('execution_progress_tracking_id');
  const inThisOrder = watch('in_this_order');
  const approversAttributes = watch('approvers_attributes');
  const approvalSchemeDefaultValues = useMemo<AuditFormApprovalSchemeValues>(
    () => ({
      execution_progress_tracking_id: executionProgressTrackingId ?? undefined,
      in_this_order: !!inThisOrder,
      approvers_attributes: approversAttributes ?? []
    }),
    [executionProgressTrackingId, inThisOrder, approversAttributes]
  );

  const ratingScaleOptions = ratingScales.map((ratingScale) => ({
    label: ratingScale.name,
    value: String(ratingScale.id),
    buttons: ratingScale.buttons,
    button_type: ratingScale.button_type,
    border: ratingScale.border
  }));

  const { t } = useTranslation();

  const isPadlockLocked = !!selectedAuditForm?.closed;

  useEffect(() => {
    const methodologyObject = makeMethodologyPracticeTree(practicesData);
    setMethodologyTreeForSelect(
      parseMethodologyPracticeTreeForPinnedToSelect(methodologyObject)
    );
  }, [practicesData]);

  return (
    <>
      <H3 variant="h3" color="coloured" mt={1}>
        {t('administrationAuditsView.basicSettings')}
      </H3>
      <Line />
      <Flex flexDirection="column" gap="10px">
        <Flex flexDirection={['column', 'row']} alignItems="center" gap="25px">
          <Flex width={['100%', '35%']} alignItems="start" gap="15px">
            <Controller
              control={control}
              name="pinned_to_id"
              render={() => {
                const pinnedToType = watch('pinned_to_type') as
                  | PinnedToType
                  | undefined;
                const pinnedToId = watch('pinned_to_id');
                const selectValue =
                  pinnedToType && pinnedToId
                    ? pinnedToSelectValue(pinnedToType, pinnedToId)
                    : undefined;
                const selected = selectValue
                  ? findOptionByValueInTree(
                      methodologyTreeForSelect,
                      selectValue
                    )
                  : undefined;

                return (
                  <NestedSelect
                    width="100%"
                    label={t('administrationAuditsView.pinTo')}
                    selectedOptions={selected ? [selected] : []}
                    setSelectedOptions={(newOption) => {
                      const parsed = parsePinnedToSelectValue(
                        newOption[0]?.value
                      );
                      setValue('pinned_to_type', parsed?.pinned_to_type, {
                        shouldValidate: true
                      });
                      setValue('pinned_to_id', parsed?.pinned_to_id, {
                        shouldValidate: true
                      });
                    }}
                    options={methodologyTreeForSelect}
                    isOneOptionSelect
                    singleSelectAnyNode
                    error={t(
                      errors?.pinned_to_id?.message ||
                        errors?.pinned_to_type?.message ||
                        ''
                    )}
                  />
                );
              }}
            />
            {showCopyButton && (
              <Flex mt="20px">
                <Button
                  variant="grey"
                  bordered
                  transparent
                  onClick={() => {
                    setShowCopyForm((prevShowCopyForm) => !prevShowCopyForm);
                  }}
                >
                  <Flex alignItems="center" gap="5px">
                    <FontAwesomeIcon icon={faCopy} /> {t('common.copy')}
                  </Flex>
                </Button>
              </Flex>
            )}
          </Flex>
          <AuditFormCodeNameField practices={practicesData} />
          <Flex width={['100%', '30%']} justifyContent="space-between" px={2}>
            <Flex flexDirection="column" alignItems="center">
              <FieldLabel>{t('administrationAuditsView.version')}</FieldLabel>
              <P variant="h3" mt={2}>
                1
              </P>
            </Flex>
            <Flex flexDirection="column" alignItems="center">
              <FieldLabel whiteSpace="nowrap">
                {t('administrationAuditsView.createDate')}:
              </FieldLabel>
              <P variant="body" mt={2}>
                {selectedAuditForm?.created_at_formatted || ''}
              </P>
            </Flex>
            <Flex flexDirection="column" alignItems="center">
              <FieldLabel whiteSpace="nowrap">
                {t('administrationAuditsView.lastChange')}:
              </FieldLabel>
              <P variant="body" mt={2}>
                {selectedAuditForm?.updated_at_formatted || ''}
              </P>
            </Flex>
          </Flex>
        </Flex>

        <Flex flexDirection={['column', 'row']} width="100%" gap="25px">
          <Controller
            control={control}
            name="area_ids"
            render={({ field: { onChange, value } }) => (
              <NestedSelect
                label={t('administrationAuditsView.usedInAreas') as string}
                selectedOptions={(value ?? [])
                  .map((vid) => findOptionByValueInTree(areas, vid))
                  .filter((o): o is OptionForNestedSelect => !!o)}
                setSelectedOptions={(newOptions) => {
                  onChange(
                    collapseSelectedAreaPermissionIds(areas, newOptions).map(
                      (id) => String(id)
                    )
                  );
                }}
                options={areas}
                width={['100%', '35%']}
              />
            )}
          />

          <Flex width={['100%', '45%']} alignItems="start" gap="10px">
            <Controller
              control={control}
              name="rating_scale_id"
              render={({ field: { onChange, value } }) => {
                const selectedRatingScale = ratingScaleOptions.find(
                  (ratingScale) => ratingScale.value === value
                );
                return (
                  <Flex
                    width="100%"
                    gap="5px"
                    flexDirection={['column', 'row']}
                  >
                    <Select
                      label={t('administrationAuditsView.ratingScale')}
                      selectedOptions={selectedRatingScale}
                      onChange={(val: IOption) => {
                        onChange(val.value);
                      }}
                      options={ratingScaleOptions}
                      withoutMargin
                      error={t(errors?.rating_scale_id?.message || '')}
                    />
                    <Flex
                      flexDirection="column"
                      minWidth="200px"
                      minHeight="60px"
                      overflow="hidden"
                    >
                      <FieldLabel>
                        {t('administrationAuditsView.ratingScalePreview')}
                      </FieldLabel>
                      <Flex height="35px">
                        <RatingScaleButtons
                          buttons={selectedRatingScale?.buttons || []}
                          showImages={
                            selectedRatingScale?.button_type === 'picture'
                          }
                          bordered={selectedRatingScale?.border}
                          borderRadius
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                );
              }}
            />
          </Flex>
          <Flex width={['100%', '20%']} flexDirection={['column', 'row']}>
            <Flex flexDirection="column" width={['100%', '80px']}>
              <Flex alignItems="center" gap="5px">
                <FieldLabel>{t('administrationAuditsView.goal')}</FieldLabel>
                <InfoTooltip
                  tooltipId="audit-form-goal-tooltip"
                  tooltipValue="administrationAuditsView.goalTooltip"
                  tooltipWidth="300px"
                />
              </Flex>
              <TextField
                width="100%"
                {...register('goal')}
                error={t(errors?.goal?.message || '')}
              />
            </Flex>

            <Flex alignItems="center" flexDirection="column" mx="auto">
              <FieldLabel width="fit-content">
                {t('administrationAuditsView.goodAboveBelow')}
              </FieldLabel>
              <Flex gap="15px" mt={2}>
                <Radio label="≥" value="(>=)" {...register('goal_range')} defaultChecked={true} />
                <Radio label="≤" value="(<=)" {...register('goal_range')} />
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Flex gap="25px" flexDirection={['column', 'row']}>
          <Flex width={['100%', '15%']}>
            <Controller
              control={control}
              name="author_id"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  disabled
                  label={t('administrationAuditsView.createdBy')}
                  selectedOptions={usersListWithAuthor.find(
                    (userOption) => userOption.value === value?.toString()
                  )}
                  onChange={(option: IOption) => {
                    onChange(option.value);
                  }}
                  options={usersListWithAuthor}
                />
              )}
            />
          </Flex>

          <Flex width={['100%', '40%']}>
            <Controller
              control={control}
              name="person_authorized_to_edit_ids"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  label={t('administrationAuditsView.authorizedToEdit')}
                  selectedOptions={
                    usersList.filter((userOption) =>
                      value?.includes(userOption.value)
                    ) || []
                  }
                  isMulti
                  onChange={(val) => {
                    onChange(
                      Array.isArray(val) ? val.map(({ value }) => value) : []
                    );
                  }}
                  options={usersList}
                  withoutMargin
                />
              )}
            />
          </Flex>

          <Flex flexDirection="column">
            <FieldLabel>{t('administrationAuditsView.active')}</FieldLabel>
            <Flex mt={2}>
              <Controller
                control={control}
                name="state"
                render={({ field: { onChange, value } }) => (
                  <SwitchButton
                    value={value === 'active'}
                    onChange={(checked) => {
                      onChange(checked ? 'active' : 'inactive');
                    }}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap="5px">
            <FieldLabel>{t('administrationAuditsView.closed?')}</FieldLabel>
            <PadlockButton
              locked={isPadlockLocked}
              onClick={() => setIsClosingModalOpen(true)}
            />
          </Flex>

          <Flex flexDirection="column">
            <FieldLabel whiteSpace="nowrap">
              {t('administrationAuditsView.requireConfirmation')}
            </FieldLabel>
            <Flex mt={2} gap="10px">
              <Controller
                control={control}
                name="requires_approval"
                render={({ field: { onChange, value } }) => (
                  <SwitchButton
                    value={!!value}
                    onChange={onChange}
                  />
                )}
              />
              <Button
                disabled={!requiresApproval}
                onClick={() => setIsApprovalSchemeModalOpen(true)}
              >
                <Flex gap="5px" alignItems="center">
                  <FontAwesomeIcon size="xl" icon={faPersonCircleCheck} />
                  {t('administrationAuditsView.panel')}
                </Flex>
              </Button>
            </Flex>
          </Flex>

          <Flex alignItems="end" gap="5px" ml="auto" pr={2}>
            <Button bordered transparent minHeight="50px" width="50px">
              <FontAwesomeIcon size="2x" icon={faUpload} />
            </Button>
            <Button bordered transparent minHeight="50px" width="50px">
              <FontAwesomeIcon size="2x" icon={faDownload} />
            </Button>
            <Button
              disabled={!selectedAuditForm}
              bordered
              transparent
              minHeight="50px"
              width="50px"
              onClick={() => setIsNotesModalOpen(true)}
            >
              <FontAwesomeIcon size="2x" icon={faClipboard} />
            </Button>
            <Button bordered transparent minHeight="50px" width="50px">
              <FontAwesomeIcon size="2x" icon={faClockRotateLeft} />
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <CopyForm
        isOpen={showCopyForm}
        onCancelClick={() => {
          setShowCopyForm(false);
        }}
        defaultValues={getValues()}
        methodologyTreeForSelect={methodologyTreeForSelect}
        areas={areas}
        usersList={usersListWithAuthor}
      />

      <NotesModal
        isOpen={isNotesModalOpen}
        onCancelClick={() => {
          setIsNotesModalOpen((prevIsNotesModalOpen) => !prevIsNotesModalOpen);
        }}
        noteableId={selectedAuditForm?.id}
        noteableType="AuditForm"
        notes={selectedAuditForm?.notes}
        setNotes={(newNotes) =>
          setSelectedAuditForm?.(
            (prevSelectedItemData) =>
              prevSelectedItemData && {
                ...prevSelectedItemData,
                notes: newNotes
              }
          )
        }
      />

      <AuditFormClosingModal
        isOpen={isClosingModalOpen}
        onCancelClick={() => setIsClosingModalOpen(false)}
        watch={watch}
        setValue={setValue}
        usersList={usersListWithAuthor}
        selectedAuditForm={selectedAuditForm}
        setSelectedAuditForm={setSelectedAuditForm}
      />

      <AuditFormApprovalSchemeModal
        isOpen={isApprovalSchemeModalOpen}
        onCancelClick={() => setIsApprovalSchemeModalOpen(false)}
        defaultValues={approvalSchemeDefaultValues}
        onSave={(values) => {
          setValue(
            'execution_progress_tracking_id',
            values.execution_progress_tracking_id ?? null
          );
          setValue('in_this_order', !!values.in_this_order);
          setValue('approvers_attributes', values.approvers_attributes ?? []);
        }}
      />
    </>
  );
};
